import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BudgetAlertsPanel = ({ alerts }) => {
  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical':
        return 'AlertTriangle';
      case 'warning':
        return 'AlertCircle';
      case 'info':
        return 'Info';
      default:
        return 'Bell';
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'critical':
        return 'border-l-error bg-error/5';
      case 'warning':
        return 'border-l-warning bg-warning/5';
      case 'info':
        return 'border-l-primary bg-primary/5';
      default:
        return 'border-l-muted bg-muted/5';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'over_budget':
        return 'TrendingUp';
      case 'approaching_limit':
        return 'Clock';
      case 'approval_pending':
        return 'FileCheck';
      case 'under_utilized':
        return 'TrendingDown';
      default:
        return 'Bell';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleAlertAction = (alertId, action) => {
    console.log(`Alert ${alertId} - Action: ${action}`);
    // Implement alert action logic
  };

  const handleViewAll = () => {
    console.log('Viewing all alerts');
    // Navigate to full alerts page
  };

  return (
    <div className="bg-card border border-border rounded-lg card-shadow">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Bell" size={20} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Budget Alerts</h3>
            <p className="text-sm text-muted-foreground">{alerts?.length} active alerts</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          iconName="ExternalLink"
          onClick={handleViewAll}
          className="text-xs"
        >
          View All
        </Button>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {alerts?.length > 0 ? (
          <div className="space-y-1">
            {alerts?.map((alert) => (
              <div
                key={alert?.id}
                className={`border-l-4 p-4 hover:bg-muted/5 transition-colors ${getAlertColor(alert?.type)}`}
              >
                <div className="flex items-start justify-between space-x-3">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex-shrink-0 mt-0.5">
                      <Icon 
                        name={getAlertIcon(alert?.type)} 
                        size={16} 
                        className={`${
                          alert?.type === 'critical' ? 'text-error' :
                          alert?.type === 'warning' ? 'text-warning' :
                          alert?.type === 'info'? 'text-primary' : 'text-muted-foreground'
                        }`} 
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-semibold text-foreground truncate">
                          {alert?.title}
                        </h4>
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          <Icon name={getCategoryIcon(alert?.category)} size={12} className="text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">{alert?.department}</span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {alert?.message}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center space-x-4">
                          <span className="text-muted-foreground font-mono">
                            {alert?.budgetCode}
                          </span>
                          {alert?.amount && (
                            <span className="font-medium text-foreground">
                              {formatCurrency(alert?.amount)}
                            </span>
                          )}
                        </div>
                        <span className="text-muted-foreground">
                          {formatTimeAgo(alert?.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    {alert?.type === 'critical' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="AlertTriangle"
                        onClick={() => handleAlertAction(alert?.id, 'urgent_action')}
                        className="text-xs text-error hover:text-error"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => handleAlertAction(alert?.id, 'view')}
                      className="text-xs"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      onClick={() => handleAlertAction(alert?.id, 'dismiss')}
                      className="text-xs"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3 opacity-50" />
            <h4 className="text-sm font-medium text-foreground mb-1">No Active Alerts</h4>
            <p className="text-xs text-muted-foreground">All budgets are within normal parameters</p>
          </div>
        )}
      </div>

      {/* Alert Summary Footer */}
      {alerts?.length > 0 && (
        <div className="border-t border-border p-4">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-error rounded-full" />
                <span className="text-muted-foreground">
                  {alerts?.filter(a => a?.type === 'critical')?.length} Critical
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-warning rounded-full" />
                <span className="text-muted-foreground">
                  {alerts?.filter(a => a?.type === 'warning')?.length} Warning
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-primary rounded-full" />
                <span className="text-muted-foreground">
                  {alerts?.filter(a => a?.type === 'info')?.length} Info
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="Settings"
              className="text-xs"
              onClick={() => console.log('Configure alert settings')}
            >
              Configure
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetAlertsPanel;